/**
 * Alert Service
 * Équivalent de alert_service.dart dans Flutter
 * Envoie des alertes critiques via webhook (Discord, Slack, etc.)
 */

import axios from 'axios'

interface AlertContext {
  [key: string]: any
}

class AlertService {
  private static instance: AlertService
  private webhookUrl = import.meta.env.VITE_ALERT_WEBHOOK_URL || ''

  private constructor() {}

  static getInstance(): AlertService {
    if (!AlertService.instance) {
      AlertService.instance = new AlertService()
    }
    return AlertService.instance
  }

  async sendCriticalAlert(params: {
    title: string
    message: string
    context?: AlertContext
  }): Promise<void> {
    try {
      console.error('🚨 CRITICAL ALERT:', {
        title: params.title,
        message: params.message,
        context: params.context,
        timestamp: new Date().toISOString(),
      })

      // If no webhook URL is configured, just log to console
      if (!this.webhookUrl) {
        console.warn('⚠️ Alert webhook URL not configured, alert logged to console only')
        return
      }

      // Send alert via webhook (Discord format example)
      const payload = {
        embeds: [
          {
            title: params.title,
            description: params.message,
            color: 15158332, // Red color
            fields: params.context
              ? Object.entries(params.context).map(([key, value]) => ({
                  name: key,
                  value: String(value),
                  inline: true,
                }))
              : [],
            timestamp: new Date().toISOString(),
            footer: {
              text: 'EASYSERVICE Alert System',
            },
          },
        ],
      }

      await axios.post(this.webhookUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 5000,
      })

      console.debug('✅ Critical alert sent successfully')
    } catch (error) {
      console.error('❌ Error sending critical alert:', error)
      // Don't throw - we don't want alert failures to break the app
    }
  }

  async sendInfoAlert(params: {
    title: string
    message: string
    context?: AlertContext
  }): Promise<void> {
    try {
      console.info('ℹ️ INFO ALERT:', {
        title: params.title,
        message: params.message,
        context: params.context,
        timestamp: new Date().toISOString(),
      })

      if (!this.webhookUrl) {
        return
      }

      const payload = {
        embeds: [
          {
            title: params.title,
            description: params.message,
            color: 3447003, // Blue color
            fields: params.context
              ? Object.entries(params.context).map(([key, value]) => ({
                  name: key,
                  value: String(value),
                  inline: true,
                }))
              : [],
            timestamp: new Date().toISOString(),
            footer: {
              text: 'EASYSERVICE Info System',
            },
          },
        ],
      }

      await axios.post(this.webhookUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 5000,
      })

      console.debug('✅ Info alert sent successfully')
    } catch (error) {
      console.error('❌ Error sending info alert:', error)
    }
  }

  async sendSuccessAlert(params: {
    title: string
    message: string
    context?: AlertContext
  }): Promise<void> {
    try {
      console.info('✅ SUCCESS ALERT:', {
        title: params.title,
        message: params.message,
        context: params.context,
        timestamp: new Date().toISOString(),
      })

      if (!this.webhookUrl) {
        return
      }

      const payload = {
        embeds: [
          {
            title: params.title,
            description: params.message,
            color: 3066993, // Green color
            fields: params.context
              ? Object.entries(params.context).map(([key, value]) => ({
                  name: key,
                  value: String(value),
                  inline: true,
                }))
              : [],
            timestamp: new Date().toISOString(),
            footer: {
              text: 'EASYSERVICE Success System',
            },
          },
        ],
      }

      await axios.post(this.webhookUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 5000,
      })

      console.debug('✅ Success alert sent successfully')
    } catch (error) {
      console.error('❌ Error sending success alert:', error)
    }
  }
}

// Export singleton instance
export const alertService = AlertService.getInstance()
